import React, { useCallback, useEffect, useRef, useState } from 'react';
import { BackArrow } from './Arrow';
import './ScreenReel.css';

// How each card sits by its distance from the front one, measured from the reference reel:
// its size, its light and whether it shows
const SCALE = [1, 0.72, 0.62, 0.52, 0.5];
const LIGHT = [1, 0.62, 0.45, 0.32, 0.32];
const SHOW = [1, 1, 1, 0.35, 0];
const TURN = 32;
const DRAG_STEP = 70;
const AUTOPLAY_MS = 5300;
const TILT = Math.cos((TURN * Math.PI) / 180);
// How much each card tucks under the one in front of it
const OVERLAP = 0.58;

const wrap = (i, n) => ((i % n) + n) % n;

// The deck is a loop: each card's steps from the front, counted the short way round,
// so there are always cards on both sides
function loopOffset(i, index, n) {
  let d = wrap(i - index, n);
  if (d > n / 2) d -= n;
  return d;
}

// Each card's distance from the centre, built outwards from the front card using real widths,
// so wide desktop screens and narrow phones sit next to each other without covering too much
function positions(widths, index) {
  const n = widths.length;
  const x = new Array(n).fill(0);
  for (const dir of [1, -1]) {
    let at = 0;
    const reach = dir === 1 ? Math.floor(n / 2) : Math.ceil(n / 2) - 1;
    for (let k = 1; k <= reach; k += 1) {
      const i = wrap(index + dir * k, n);
      const before = wrap(index + dir * (k - 1), n);
      const prev = widths[before] * (k === 1 ? 1 : SCALE[Math.min(k - 1, 4)] * TILT);
      const cur = widths[i] * SCALE[Math.min(k, 4)] * TILT;
      // Beside the front card, never tuck under more than a sliver of it
      at += k === 1 ? prev / 2 + cur / 2 - Math.min((cur * OVERLAP) / 2, prev * 0.12) : (prev / 2 + cur / 2) * OVERLAP;
      x[i] = dir * at;
    }
  }
  return x;
}

function cardStyle(offset, x, jumped) {
  const far = Math.min(Math.abs(offset), 4);
  const side = Math.sign(offset);
  return {
    transform: `translate(-50%, -50%) translateX(${x}px) rotateY(${-side * TURN}deg) scale(${SCALE[far]})`,
    filter: far ? `brightness(${LIGHT[far]})` : undefined,
    opacity: SHOW[far],
    zIndex: 50 - far,
    visibility: Math.abs(offset) > 4 ? 'hidden' : undefined,
    transition: jumped ? 'none' : undefined,
  };
}

const kindLabel = {
  desktop: 'Desktop · 16:10',
  'tablet-wide': 'Tablet · landscape',
  tablet: 'Tablet · portrait',
  phone: 'Phone · 9:16',
};
const kindName = { desktop: 'desktop', 'tablet-wide': 'tablet', tablet: 'tablet', phone: 'phone' };

export default function ScreenReel({ screens, name }) {
  const [index, setIndex] = useState(0);
  const [widths, setWidths] = useState(() => screens.map(() => 0));
  const [paused, setPaused] = useState(false);
  const [inView, setInView] = useState(false);
  const stageRef = useRef(null);
  const reelRef = useRef(null);
  const thumbsRef = useRef(null);
  const drag = useRef(null);
  const lastOffsets = useRef(null);
  const count = screens.length;

  const go = useCallback(i => setIndex(wrap(i, count)), [count]);

  useEffect(() => {
    const stage = stageRef.current;
    // Card widths come from the CSS (they follow the stage height and the screen width)
    const ro = new ResizeObserver(() => setWidths([...stage.querySelectorAll('.reel-card')].map(c => c.offsetWidth)));
    ro.observe(stage);
    // The whole reel counts, so it keeps going while you use the thumbnails under the screens
    const io = new IntersectionObserver(([e]) => setInView(e.isIntersecting));
    io.observe(reelRef.current);
    return () => {
      ro.disconnect();
      io.disconnect();
    };
  }, []);

  // Moves on by itself, like the reference: every 5.3 seconds, but never while the pointer is on it,
  // while it is off screen, or for visitors who asked for less motion
  useEffect(() => {
    if (paused || !inView || count < 2) return undefined;
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return undefined;
    const id = setTimeout(() => setIndex(i => wrap(i + 1, count)), AUTOPLAY_MS);
    return () => clearTimeout(id);
  }, [index, paused, inView, count]);

  // Keep the chosen thumbnail in view inside its strip
  useEffect(() => {
    const strip = thumbsRef.current;
    const thumb = strip?.children[index];
    if (!thumb) return;
    const left = thumb.offsetLeft - strip.clientWidth / 2 + thumb.offsetWidth / 2;
    strip.scrollTo({ left, behavior: 'smooth' });
  }, [index]);

  const onKey = e => {
    if (e.key === 'ArrowRight') go(index + 1);
    if (e.key === 'ArrowLeft') go(index - 1);
  };

  const onPointerDown = e => {
    drag.current = { x: e.clientX, moved: false };
  };

  const onPointerMove = e => {
    const d = drag.current;
    if (!d) return;
    const dx = e.clientX - d.x;
    if (Math.abs(dx) < DRAG_STEP) return;
    if (!d.moved) {
      d.moved = true;
      e.currentTarget.setPointerCapture(e.pointerId);
    }
    d.x = e.clientX;
    setIndex(i => wrap(i + (dx < 0 ? 1 : -1), count));
  };

  const endDrag = () => {
    // The click that follows a drag must not also pick a card
    if (drag.current?.moved) setTimeout(() => { drag.current = null; }, 0);
    else drag.current = null;
  };

  const current = screens[index];
  const xs = positions(widths, index);
  const offsets = screens.map((_, i) => loopOffset(i, index, count));
  // A card that came round the back of the loop jumps to its new side instead of flying across
  const jumped = offsets.map((o, i) => lastOffsets.current != null && Math.abs(o - lastOffsets.current[i]) > count / 2);

  useEffect(() => {
    lastOffsets.current = offsets;
  });

  return (
    <section ref={reelRef} className="reel" aria-label={`Screens from ${name}`}>
      <div
        className="reel-stage"
        onPointerEnter={e => e.pointerType === 'mouse' && setPaused(true)}
        onPointerLeave={() => setPaused(false)}
      >
        <img className="reel-glow" src={current.thumb} alt="" aria-hidden="true" />
        <span className="reel-vignette" aria-hidden="true" />
        <span className="reel-floor" aria-hidden="true" />

        <div
          ref={stageRef}
          className="reel-deck"
          tabIndex={0}
          onKeyDown={onKey}
          // Hold still for keyboard users while they look, but a mouse click must not stop it
          onFocus={e => e.target.matches(':focus-visible') && setPaused(true)}
          onBlur={() => setPaused(false)}
          onPointerDown={onPointerDown}
          onPointerMove={onPointerMove}
          onPointerUp={endDrag}
          onPointerCancel={endDrag}
          aria-roledescription="carousel"
        >
          {screens.map((s, i) => {
            const offset = offsets[i];
            return (
              <button
                key={s.src}
                type="button"
                className={`reel-card reel-${s.kind}`}
                style={cardStyle(offset, xs[i], jumped[i])}
                onClick={() => {
                  if (!drag.current?.moved) go(i);
                }}
                tabIndex={-1}
                aria-label={`${s.label}, ${kindName[s.kind]} screen ${i + 1} of ${count}`}
                aria-current={offset === 0 ? 'true' : undefined}
              >
                {/* The fade alternates between two identical animations so it replays on every jump */}
                <span className="reel-frame" style={jumped[i] ? { animationName: `reelIn${index % 2}` } : undefined}>
                  {s.kind === 'desktop' && <span className="reel-bar" aria-hidden="true"><i /><i /><i /></span>}
                  <img
                    src={s.src}
                    alt={`${name}: ${s.label}`}
                    draggable="false"
                    loading={Math.abs(offset) <= 3 ? 'eager' : 'lazy'}
                    decoding="async"
                  />
                </span>
              </button>
            );
          })}
        </div>
      </div>

      <div className="shell reel-bar-row">
        <div className="reel-count">
          <span className="reel-num">{String(index + 1).padStart(2, '0')}</span>
          <span className="meta reel-of">/ {String(count).padStart(2, '0')}</span>
          <span className="meta reel-kind">{current.label} · {kindLabel[current.kind]}</span>
        </div>

        <div ref={thumbsRef} className="reel-thumbs">
          {screens.map((s, i) => (
            <button
              key={s.thumb}
              type="button"
              className={`reel-thumb reel-thumb-${s.kind} ${i === index ? 'on' : ''}`}
              onClick={() => go(i)}
              aria-label={`Show ${s.label}, ${kindName[s.kind]}`}
              aria-current={i === index ? 'true' : undefined}
            >
              <img src={s.thumb} alt="" loading="lazy" />
            </button>
          ))}
        </div>

        <div className="reel-nav">
          <button type="button" onClick={() => go(index - 1)} aria-label="Previous screen">
            <BackArrow />
          </button>
          <button type="button" onClick={() => go(index + 1)} aria-label="Next screen">
            <BackArrow />
          </button>
        </div>
      </div>
      <span className="reel-progress" style={{ '--p': (index + 1) / count }} aria-hidden="true" />
    </section>
  );
}
